/**
 * Durable storage for takes, and recovery of the ones an interruption took away.
 *
 * A take used to live only in a `Blob` on the recorder instance. That is fine right up to
 * the moment iOS backgrounds the tab — which it does for a phone call, for the share sheet,
 * for a notification the user taps — at which point `pagehide` fires, the recorder aborts,
 * and the audio a person just performed is gone with no record that it ever existed. The
 * brief names that exact sequence as unacceptable, so the bytes go to disk *while they are
 * being captured*, not afterwards.
 *
 * The shape:
 *
 * - A row in `recordings` is created before the first chunk, in state `recording`.
 * - Every chunk `MediaRecorder` hands over is appended to a durable blob writer, so at any
 *   instant what is on disk is everything that has arrived.
 * - `finish()` moves the row to `finished` and records the real duration.
 * - The row survives until the take is saved into the library or the user discards it.
 *
 * At launch, anything still in `recording` was interrupted and anything in `finished` was
 * never saved; both are offered back. Nothing here is on the audio path — the writer is
 * awaited off a queue, and a slow write can only ever delay the *next* append.
 */

import { createLogger, tag } from '../core/log';
import { newUUID, nowISODate, type ISODate, type UUID } from '../model/types';
import type { BlobStore, BlobWriter } from './blob-store';
import { readStore, takeKey, writeStore, type RecordingRecord } from './db';

const log = createLogger('takes');

function errorName(error: unknown): string {
  return error instanceof Error ? error.name : 'unknown';
}

/** Rows older than this are swept at launch: the audio behind them is long since stale. */
const RECOVERY_TTL_MS = 7 * 24 * 60 * 60 * 1000;

export interface RecoverableTake {
  id: UUID;
  name: string;
  /** `true` when the recording was cut short rather than finished normally. */
  interrupted: boolean;
  mimeType: string;
  fileExtension: string;
  duration: number;
  byteSize: number;
  startedAt: ISODate;
  blobKey: string;
}

export interface OpenTakeSink {
  readonly id: UUID;
  /** Queues a chunk. Never throws at the caller; a failed write is logged and the row marked. */
  append(chunk: Blob): void;
  /** Flushes, closes and marks the row finished. Resolves to the stored byte count. */
  finish(duration: number): Promise<number>;
  /** Throws the whole thing away — bytes and row. */
  discard(): Promise<void>;
}

/**
 * Serialises appends onto one promise chain. `MediaRecorder` delivers a chunk every 500 ms
 * and a durable IndexedDB write can outlive that on a busy phone, so overlapping writes
 * would interleave chunk indices and corrupt the file.
 */
class TakeSink implements OpenTakeSink {
  readonly id: UUID;
  private readonly blobs: BlobStore;
  private readonly key: string;
  private readonly mimeType: string;
  private writer: BlobWriter | null = null;
  private queue: Promise<void> = Promise.resolve();
  private bytes = 0;
  private chunkCount = 0;
  private closed = false;
  private failed = false;

  constructor(blobs: BlobStore, id: UUID, key: string, mimeType: string, writer: BlobWriter) {
    this.blobs = blobs;
    this.id = id;
    this.key = key;
    this.mimeType = mimeType;
    this.writer = writer;
  }

  append(chunk: Blob): void {
    if (this.closed || this.failed || chunk.size === 0) return;
    this.chunkCount += 1;
    this.queue = this.queue.then(async () => {
      const writer = this.writer;
      if (writer === null || this.closed || this.failed) return;
      try {
        await writer.write(chunk);
        this.bytes += chunk.size;
        await this.touch({ byteSize: this.bytes, chunkCount: this.chunkCount });
      } catch (error) {
        // Out of space, or the store went away. The chunks already on disk stay: a partial
        // take a user can still hear beats a complete one they cannot.
        this.failed = true;
        log.error('take chunk could not be stored', error, { take: tag(this.id) });
      }
    });
  }

  async finish(duration: number): Promise<number> {
    if (this.closed) return this.bytes;
    this.closed = true;
    await this.queue;
    const writer = this.writer;
    this.writer = null;
    if (writer !== null) {
      try {
        const stat = await writer.close();
        this.bytes = stat.byteSize;
      } catch (error) {
        log.error('take could not be closed', error, { take: tag(this.id) });
      }
    }
    await this.touch({ state: 'finished', duration, byteSize: this.bytes, chunkCount: this.chunkCount });
    return this.bytes;
  }

  async discard(): Promise<void> {
    this.closed = true;
    await this.queue.catch(() => undefined);
    const writer = this.writer;
    this.writer = null;
    if (writer !== null) await writer.abort().catch(() => undefined);
    await this.blobs.delete(this.key).catch(() => undefined);
    await writeStore('recordings', (store) => store.delete(this.id)).catch(() => undefined);
  }

  private async touch(change: Partial<RecordingRecord>): Promise<void> {
    try {
      const existing = await readStore('recordings', (store) => store.get(this.id));
      if (existing === undefined) return;
      await writeStore('recordings', (store) =>
        store.put({ ...existing, ...change, mimeType: this.mimeType, updatedAt: nowISODate() }),
      );
    } catch (error) {
      log.warn('take row could not be updated', { take: tag(this.id), reason: tag(errorName(error)) });
    }
  }
}

/**
 * Opens a durable sink for one take.
 *
 * `durable: true` on the writer is what makes this worth doing: it flushes every chunk
 * rather than batching into 4 MB blocks, so the bytes are on disk within milliseconds of
 * leaving the encoder instead of sitting in a buffer the tab discard takes with it.
 */
export async function openTake(
  blobs: BlobStore,
  projectID: UUID,
  options: { mimeType: string; fileExtension: string; sampleRate: number; channelCount: number; name?: string },
): Promise<OpenTakeSink | null> {
  const id = newUUID();
  const key = takeKey(id);
  try {
    const writer = await blobs.createWriter(key, { mimeType: options.mimeType, durable: true });
    const row: RecordingRecord = {
      id,
      projectID,
      state: 'recording',
      startedAt: nowISODate(),
      updatedAt: nowISODate(),
      blobKey: key,
      mimeType: options.mimeType,
      fileExtension: options.fileExtension,
      sampleRate: options.sampleRate,
      channelCount: options.channelCount,
      duration: 0,
      byteSize: 0,
      chunkCount: 0,
      peakLevel: 0,
      ...(options.name === undefined ? {} : { name: options.name }),
    };
    await writeStore('recordings', (store) => store.put(row));
    return new TakeSink(blobs, id, key, options.mimeType, writer);
  } catch (error) {
    // Recording without a safety net is still better than not recording.
    log.error('take could not be opened for durable capture', error);
    return null;
  }
}

/** Everything a launch should offer back, newest first. */
export async function recoverableTakes(projectID: UUID): Promise<RecoverableTake[]> {
  let rows: RecordingRecord[];
  try {
    rows = await readStore('recordings', (store) => store.index('by-project').getAll(projectID));
  } catch (error) {
    log.warn('recordings could not be read', { reason: tag(errorName(error)) });
    return [];
  }
  const cutoff = Date.now() - RECOVERY_TTL_MS;
  const live: RecoverableTake[] = [];
  for (const row of rows) {
    if (row.state === 'abandoned' || Date.parse(row.updatedAt) < cutoff) {
      await discardTakeRow(row.id).catch(() => undefined);
      continue;
    }
    if (row.byteSize <= 0) {
      await discardTakeRow(row.id).catch(() => undefined);
      continue;
    }
    live.push({
      id: row.id,
      name: row.name ?? 'Recovered take',
      interrupted: row.state === 'recording',
      mimeType: row.mimeType,
      fileExtension: row.fileExtension,
      duration: row.duration,
      byteSize: row.byteSize,
      startedAt: row.startedAt,
      blobKey: row.blobKey,
    });
  }
  live.sort((left, right) => Date.parse(right.startedAt) - Date.parse(left.startedAt));
  return live;
}

/** Removes one row and the audio behind it. Used when a take is saved, or thrown away. */
export async function discardTakeRow(id: UUID, blobs?: BlobStore): Promise<void> {
  const row = await readStore('recordings', (store) => store.get(id)).catch(() => undefined);
  if (row !== undefined && blobs !== undefined) await blobs.delete(row.blobKey).catch(() => undefined);
  await writeStore('recordings', (store) => store.delete(id)).catch(() => undefined);
}

/**
 * Parks a blob that never became an editable take — a container this browser wrote and then
 * refused to decode. It cannot be edited here, but it can still be exported, and losing it
 * silently is the one outcome that is not allowed.
 */
export async function parkUndecodableTake(
  blobs: BlobStore,
  projectID: UUID,
  blob: Blob,
  options: { mimeType: string; fileExtension: string; duration: number },
): Promise<UUID | null> {
  const id = newUUID();
  const key = takeKey(id);
  try {
    const stat = await blobs.put(key, blob, { mimeType: options.mimeType });
    const row: RecordingRecord = {
      id,
      projectID,
      state: 'finished',
      startedAt: nowISODate(),
      updatedAt: nowISODate(),
      blobKey: key,
      mimeType: options.mimeType,
      fileExtension: options.fileExtension,
      sampleRate: 0,
      channelCount: 0,
      duration: options.duration,
      byteSize: stat.byteSize,
      chunkCount: 1,
      peakLevel: 0,
      name: 'Unreadable take',
    };
    await writeStore('recordings', (store) => store.put(row));
    return id;
  } catch (error) {
    log.error('undecodable take could not be parked', error);
    return null;
  }
}
